import { fetchDayRecords, type DailyRecord } from '@/services/records';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const EMPTY_RECORD: DailyRecord = {
  date: '',
  morningStatus: 'pending',
  morningTime: null,
  morningDelta: null,
  bedtimeStatus: 'pending',
  bedtimeTime: null,
  bedtimeDelta: null,
};

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    confirmed_early: 'Confirmed Early',
    on_time: 'On Time',
    late: 'Late',
    missed: 'Missed',
    shabbat: 'Shabbat 🕯️',
    holiday: 'Holiday',
    pending: 'Pending...',
  };
  return labels[status] || status;
}

function statusStyle(status: string) {
  const styles: Record<string, { bg: string; text: string; dot: string }> = {
    confirmed_early: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-400' },
    on_time: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    late: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
    missed: { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-700', dot: 'bg-rose-400' },
    shabbat: { bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-300' },
    holiday: { bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-400' },
    pending: { bg: 'bg-background-100 border-background-200', text: 'text-foreground-500', dot: 'bg-foreground-300' },
  };
  return styles[status] || styles.pending;
}

function deltaText(delta: number | null): string {
  if (delta === null) return '';
  if (delta === 0) return 'Exactly on time';
  if (delta < 0) return `${Math.abs(delta)} min early`;
  return `${delta} min late`;
}

function deltaColor(delta: number | null): string {
  if (delta === null) return 'text-foreground-400';
  if (delta === 0) return 'text-emerald-600';
  if (delta < 0) return 'text-emerald-600';
  return 'text-amber-600';
}

export default function TodayStatusWidget() {
  const [record, setRecord] = useState<DailyRecord>(EMPTY_RECORD);
  const [schedules, setSchedules] = useState<{ morning: string; bedtime: string }>({ morning: '', bedtime: '' });

  useEffect(() => {
    let mounted = true;
    const todayStr = toDateStr(new Date());
    fetchDayRecords(todayStr, todayStr).then((recs) => {
      if (mounted && recs[todayStr]) setRecord(recs[todayStr]);
    });
    supabase
      .from('user_habits')
      .select('custom_settings, habit_types(name, custom_settings)')
      .eq('enabled', true)
      .then(({ data }) => {
        if (!mounted || !data) return;
        const next = { morning: '', bedtime: '' };
        for (const row of data as unknown as Array<{ custom_settings: Record<string, unknown> | null; habit_types: { name: string; custom_settings: Record<string, unknown> | null } | null }>) {
          const name = row.habit_types?.name || '';
          const hhmm = (row.custom_settings?.fixed_time as string) || (row.habit_types?.custom_settings?.ideal_time as string) || '';
          if (!hhmm) continue;
          const [h, m] = hhmm.split(':').map(Number);
          if (isNaN(h) || isNaN(m)) continue;
          const display = `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
          if (/morning/i.test(name)) next.morning = display;
          if (/bedtime|night/i.test(name)) next.bedtime = display;
        }
        setSchedules(next);
      });
    return () => { mounted = false; };
  }, []);

  const morning = record;
  const morningStyle = statusStyle(morning.morningStatus);
  const bedtimeStyle = statusStyle(morning.bedtimeStatus);
  const todayDisplay = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
        <span className="w-6 h-6 flex items-center justify-center">
          <i className="ri-check-double-line text-emerald-500 text-lg" />
        </span>
        <h3 className="text-sm font-heading font-semibold text-foreground-950">Today's Status</h3>
        <span className="text-[10px] text-foreground-400 font-mono ml-auto">{todayDisplay}</span>
      </div>

      <div className="p-4 space-y-3">
        {/* Morning */}
        <div className={`rounded-lg border p-3 ${morningStyle.bg}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center">
                <i className="ri-sun-line text-amber-500" />
              </span>
              <div>
                <p className="text-xs font-medium text-foreground-700">Morning Reminder</p>
                {schedules.morning ? (
                  <p className="text-[10px] text-foreground-400">{`Scheduled ${schedules.morning}`}</p>
                ) : (
                  <Link to="/habits" className="inline-flex items-center gap-1 text-[10px] font-medium text-primary-600 hover:text-primary-700 transition">
                    <i className="ri-settings-3-line" /> Tap to configure <i className="ri-arrow-right-line" />
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${morningStyle.dot}`} />
              <span className={`text-xs font-semibold ${morningStyle.text}`}>
                {statusLabel(morning.morningStatus)}
              </span>
            </div>
          </div>
          {morning.morningTime && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-foreground-400">Confirmed:</span>
              <span className="text-foreground-800 font-mono font-medium">{morning.morningTime}</span>
              <span className={`font-medium ${deltaColor(morning.morningDelta)}`}>
                ({deltaText(morning.morningDelta)})
              </span>
            </div>
          )}
        </div>

        {/* Bedtime */}
        <div className={`rounded-lg border p-3 ${bedtimeStyle.bg}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center">
                <i className="ri-moon-line text-indigo-400" />
              </span>
              <div>
                <p className="text-xs font-medium text-foreground-700">Bedtime Reminder</p>
                {schedules.bedtime ? (
                  <p className="text-[10px] text-foreground-400">{`Scheduled ${schedules.bedtime}`}</p>
                ) : (
                  <Link to="/habits" className="inline-flex items-center gap-1 text-[10px] font-medium text-primary-600 hover:text-primary-700 transition">
                    <i className="ri-settings-3-line" /> Tap to configure <i className="ri-arrow-right-line" />
                  </Link>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${bedtimeStyle.dot}`} />
              <span className={`text-xs font-semibold ${bedtimeStyle.text}`}>
                {statusLabel(morning.bedtimeStatus)}
              </span>
            </div>
          </div>
          {morning.bedtimeTime && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-foreground-400">Confirmed:</span>
              <span className="text-foreground-800 font-mono font-medium">{morning.bedtimeTime}</span>
              <span className={`font-medium ${deltaColor(morning.bedtimeDelta)}`}>
                ({deltaText(morning.bedtimeDelta)})
              </span>
            </div>
          )}
        </div>

        {/* Quick summary */}
        <div className="flex items-center gap-4 pt-2 border-t border-background-200/60">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-foreground-400">Morning:</span>
            <span className={`text-xs font-semibold ${morningStyle.text}`}>{statusLabel(morning.morningStatus)}</span>
          </div>
          <div className="w-px h-3 bg-background-300" />
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-foreground-400">Bedtime:</span>
            <span className={`text-xs font-semibold ${bedtimeStyle.text}`}>{statusLabel(morning.bedtimeStatus)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}